import { CssBaseline } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { MaterialCircularProgress } from 'src/components/material/CircularProgress';
import { PathRoutes } from 'src/const/Routes/PathRoutes';
import useUserSession from 'src/hooks/UserSession/UserSession';
import { Profile } from 'src/pages/Profile/Profile';
import './amplifyConfig';
import { Sidebar } from './components/common/Sidebar/Sidebar';
import { CreateCircle } from './pages/CreateCircle/CreateCircle';
import ProtectedRoute from './routing/ProtectedRoute';
import MainTemplate from './templates/Main';

import { themeColors, themeTypography } from './theme';

import { useEffect } from 'react';
import { Environment } from 'src/const/constants';
import { Oth360AuthRedirectPage } from 'src/pages/Oth360InvitePage/Oth360AuthRedirectPage';
import { Oth360FormPage } from 'src/pages/Oth360InvitePage/Oth360FormPage';
import { Oth360InvitePage } from 'src/pages/Oth360InvitePage/Oth360InvitePage';
import { OthRegistrationPage } from 'src/pages/OthRegistrationPage/OthRegistrationPage';
import { useAppDispatch } from './hooks/Redux/useAppDispatch';
import useAppSelector from './hooks/Redux/useAppSelector';
import { Admin360Page } from './pages/Admin360Page/Admin360Page';
import { AdminPage } from './pages/AdminPage/AdminPage';
import { AIAssistantPage } from './pages/AIAssistant/AIAssistantPage';
import { Auction } from './pages/Auction/Auction';
import { Auth } from './pages/Auth/Auth';
import { ChallengePage } from './pages/ChallengePage/ChallengePage';
import { DevelopmentPlan } from './pages/DevelopmentPlan/DevelopmentPlan';
import { EditCircle } from './pages/EditCircle/EditCircle';
import { EditProfile } from './pages/EditProfile/EditProfile';
import { FeedPage } from './pages/FeedPage/FeedPage';
import { Oth360ResultsPage } from './pages/Oth360InvitePage/Oth360ResultsPage';
import { UserCircle } from './pages/UserCircle/UserCircle';

import {
  fetchLoggedUser,
  selectLoggedUserInitialized,
} from './redux/slices/loggedUserSlice';
import { currentUser } from './redux/slices/userSlice';
import BlankTemplate from './templates/Blank';
const theme = createTheme({
  palette: { ...themeColors },
  typography: { ...themeTypography },
});

export const App = () => {
  const { loading, user } = useUserSession();
  const loggedUserInitialized = useAppSelector(selectLoggedUserInitialized);

  const getCurrentUser = useAppSelector(currentUser);

  const location = useLocation();
  const navigate = useNavigate();

  const dispatch = useAppDispatch();
  const shouldDisplaySidebar =
    ![
      PathRoutes.LoginRoute,
      PathRoutes.CreateProfileRoute,
      PathRoutes.Oth360InviteRoute,
      PathRoutes.Oth360AuthRoute,
      PathRoutes.Oth360FormRoute,
      PathRoutes.Oth360ResultsRoute,
      PathRoutes.OthDevelopmentPlanRoute,
    ].includes(location.pathname) &&
    !location.pathname.includes('/oth/registration');

  useEffect(() => {
    if (!loggedUserInitialized && getCurrentUser) {
      dispatch(fetchLoggedUser());
    }
  }, [dispatch, loggedUserInitialized, getCurrentUser, navigate]);

  if (!loggedUserInitialized && getCurrentUser !== null) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <MaterialCircularProgress />
      </div>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />

      {shouldDisplaySidebar && <Sidebar />}

      {loading ? (
        <MaterialCircularProgress />
      ) : (
        <Routes>
          {/* Root Route Redirects */}
          <Route
            path="/"
            element={
              user ? (
                <Navigate to={PathRoutes.FeedPageRoute} />
              ) : (
                <Navigate to={PathRoutes.LoginRoute} />
              )
            }
          />

          {/* Public Routes */}
          <Route element={<BlankTemplate />}>
            <Route
              path={PathRoutes.LoginRoute}
              element={
                user ? <Navigate to={PathRoutes.FeedPageRoute} /> : <Auth />
              }
            />
          </Route>
          <Route element={<MainTemplate />}>
            <Route path={PathRoutes.AuctionRoute} element={<Auction />} />
            <Route
              path={PathRoutes.Oth360InviteRoute}
              element={<Oth360InvitePage />}
            />
            <Route
              path={PathRoutes.Oth360AuthRoute}
              element={<Oth360AuthRedirectPage />}
            />
            <Route
              path={PathRoutes.Oth360FormRoute}
              element={<Oth360FormPage />}
            />
            <Route
              path={PathRoutes.Oth360ResultsRoute}
              element={<Oth360ResultsPage />}
            />
            <Route
              path={PathRoutes.OthRegistrationRoute}
              element={<OthRegistrationPage />}
            />
          </Route>

          {/* Protected Routes */}
          <Route element={<ProtectedRoute user={user} />}>
            {/* Main navigation routes */}
            <Route element={<MainTemplate />}>
              <Route path={PathRoutes.FeedPageRoute} element={<FeedPage />} />
              <Route path={PathRoutes.ProfileRoute} element={<Profile />} />
              <Route
                path={PathRoutes.OthDevelopmentPlanRoute}
                element={<DevelopmentPlan />}
              />

              {/* Admin and WIP routes */}
              <Route path={PathRoutes.AdminPageRoute} element={<AdminPage />} />
              <Route
                path={PathRoutes.Admin360PageRoute}
                element={<Admin360Page />}
              />
              {process.env.REACT_APP_API_ENV !== Environment.PROD && (
                <Route
                  path={PathRoutes.AIAssistantRoute}
                  element={<AIAssistantPage />}
                />
              )}
              {/* TODO clean up feature specific routing */}
              <Route
                path={PathRoutes.ChallengePageRoute}
                element={<ChallengePage />}
              />
              <Route path={PathRoutes.UserCircle} element={<UserCircle />} />
              <Route
                path={PathRoutes.CreateCircleRoute}
                element={<CreateCircle />}
              />
              <Route
                path={PathRoutes.EditCircleRoute}
                element={<EditCircle />}
              />
              <Route
                path={PathRoutes.EditProfileRoute}
                element={<EditProfile />}
              />
            </Route>
          </Route>

          {/* TODO add a proper 404 page when updating theme */}
          <Route path="*" element={<Navigate replace to="/" />} />
        </Routes>
      )}
    </ThemeProvider>
  );
};
